import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { fetchDossierAssistance } from "../../../store/actions/assistancesActions"
import { useAssistances } from "./AssistancesContext"
import {
	CarteMissionDepliee,
	ChevronMission,
	useDepliage,
} from "./MissionDepliable"
import { chf } from "../couvertures/couverturesUi"
import SelecteurCibleModal from "../couvertures/SelecteurCibleModal"
import {
	BandeauOmbre,
	EtatBadge,
	Mono,
	RoleChip,
	VTag,
	dateCourte,
	montant,
	texteInterchangeable,
	ui,
} from "./assistancesUi"

// Panneau Dossier (GE-57, maquette « tiroir latéral ») : en-tête, lignes
// Ordres ↔ missions, alertes (propositions ouvertes), chronologie
// ✉ e-mail · ⚙ auto · 👤 humain · 🚚 terrain (jalons dérivés des missions).
// Sous les modals (zIndex 1000) : une proposition s'ouvre par-dessus.

const ICONES = { message: "✉", auto: "⚙", humain: "👤", terrain: "🚚" }

const minutesRestantes = (iso) =>
	Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 60000))

// Ordre sans mission : en grâce R4d, en attente de clôture, frais, ou rien.
const etatSansMission = (ordre, chronologie) => {
	if (ordre.fin_grace)
		return {
			attente: true,
			texte: `En attente de l'ordre de dépannage (encore ${minutesRestantes(
				ordre.fin_grace,
			)} min)`,
		}
	const attente = chronologie
		.filter(
			(e) =>
				e.details &&
				typeof e.details.cle === "string" &&
				e.details.cle.startsWith(`attente:${ordre.id}:`),
		)
		.pop()
	if (attente && ["nouveau", "planifie"].includes(ordre.statut))
		return {
			attente: true,
			texte: `Attend la clôture de M${attente.mission_id}`,
		}
	if (ordre.role === "frais") return { texte: "Pas une mission (frais)" }
	return { texte: "Aucune mission liée" }
}

// Mobile (maquette ≤ 720 px) : ordre ↓ mission empilés.
const useEtroit = () => {
	const [etroit, setEtroit] = useState(window.innerWidth <= 720)
	useEffect(() => {
		const f = () => setEtroit(window.innerWidth <= 720)
		window.addEventListener("resize", f)
		return () => window.removeEventListener("resize", f)
	}, [])
	return etroit
}

// Ordre encore à servir : sa mission peut être changée à la main (Jordan
// 09.10).
const ORDRE_ACTIF = ["nouveau", "planifie"]

const LigneOrdre = ({
	ordre,
	chronologie,
	etroit,
	onClose,
	interchangeables = [],
	onChanger,
	peutLier = true,
}) => {
	const m = ordre.mission
	// GE-60 (C3) : ordre concrétisé en couverture (sans mission).
	const couv =
		!m &&
		ordre.couverture &&
		["active", "proposee"].includes(ordre.couverture.statut)
			? ordre.couverture
			: null
	const sans = m ? null : etatSansMission(ordre, chronologie)
	const [deplie, basculer] = useDepliage()
	return (
		<>
			<div style={etroit ? st.omEtroit : st.om}>
				<div>
					<div style={st.r1}>
						<Mono>{ordre.ref_ordre || ordre.role_label}</Mono>
						<RoleChip role={ordre.role} />
						{ordre.version_courante > 1 && (
							<VTag>v{ordre.version_courante}</VTag>
						)}
					</div>
					<div style={st.r2}>
						{dateCourte(ordre.recu_le)} ·{" "}
						<b style={{ color: COLORS.text, fontWeight: 600 }}>
							{montant(ordre.montant, ordre.devise)}
						</b>{" "}
						· {ordre.statut_label}
					</div>
					{peutLier &&
						ORDRE_ACTIF.includes(ordre.statut) &&
						["depannage", "transport"].includes(ordre.role) && (
							<button
								type="button"
								style={{ ...ui.lien, fontSize: 12 }}
								onClick={() => onChanger(ordre)}
							>
								{m
									? "Changer la mission liée…"
									: "Lier à une mission / fiche…"}
							</button>
						)}
				</div>
				<div style={etroit ? st.flecheEtroite : st.fleche}>
					{etroit ? "↓" : "→"}
				</div>
				{couv ? (
					<div style={st.mchip}>
						<div style={st.m1}>
							{couv.statut === "active"
								? "✓ Couverture"
								: "Couverture proposée"}
							{couv.plafond !== null
								? ` · ${chf(couv.plafond)}`
								: ""}
						</div>
						<div style={st.m2}>
							{couv.fiche_id ? (
								<Link
									to={`/depannage/fiche/${couv.fiche_id}#couvertures`}
									style={{
										color: COLORS.accent,
										textDecoration: "none",
										fontWeight: 500,
									}}
									onClick={onClose}
								>
									Voir sur la fiche N° {couv.fiche_id} →
								</Link>
							) : couv.mission_id ? (
								`sur la mission M${couv.mission_id}`
							) : null}
						</div>
					</div>
				) : m ? (
					<div style={st.mchip}>
						<div style={st.m1}>
							<ChevronMission
								ouvert={deplie}
								onClick={basculer}
								missionId={m.id}
							/>
							M{m.id}{" "}
							<EtatBadge etat={m.etat} label={m.etat_label} />
						</div>
						<div style={st.m2}>
							{[
								m.type === 1 ? "transport" : "intervention",
								m.depanneur,
								ordre.lien === "manuel" ? "lien manuel" : null,
							]
								.filter(Boolean)
								.join(" · ")}
						</div>
					</div>
				) : (
					<div style={sans.attente ? st.mchipAttente : st.mchipVide}>
						{sans.attente ? "⏳ " : ""}
						{sans.texte}
					</div>
				)}
			</div>
			{interchangeables.map((x) => (
				<div key={`inter_${x.mission}`} style={st.interchangeable}>
					⇄ {texteInterchangeable(x)}
				</div>
			))}
			{/* Carte complète de la mission, sous la ligne ordre → mission. */}
			{m && deplie && <CarteMissionDepliee missionId={m.id} />}
		</>
	)
}

const Alerte = ({ p, onOuvrir }) => (
	<div style={st.alerte}>
		<span>⚠</span>
		<div style={{ minWidth: 0 }}>
			<div style={{ fontWeight: 700, fontSize: 13.5 }}>
				{p.type_label}
				{p.simulee ? (
					<span style={{ color: COLORS.textMuted, fontWeight: 500 }}>
						{" "}
						· simulée
					</span>
				) : null}
			</div>
			<div style={{ fontSize: 12.5, color: COLORS.textMuted }}>
				{p.operations
					.slice(0, 2)
					.map((o) => o.libelle)
					.join(" · ")}
				{p.operations.length > 2 ? " …" : ""}
			</div>
		</div>
		<button
			type="button"
			style={{ ...ui.btn("primary"), marginLeft: "auto" }}
			onClick={onOuvrir}
		>
			Ouvrir
		</button>
	</div>
)

const DossierPanneau = ({ dossierId, onClose }) => {
	const dispatch = useDispatch()
	const ctx = useAssistances()
	const [d, setD] = useState(null)
	const monte = useRef(true)
	useEffect(
		() => () => {
			monte.current = false
		},
		[],
	)

	// Changement de dossier : pas d'ancien dossier affiché pendant le
	// chargement (GE-59).
	useEffect(() => {
		setD(null)
	}, [dossierId])

	useEffect(() => {
		dispatch(fetchDossierAssistance(dossierId)).then((data) => {
			if (monte.current) setD(data || false)
		})
	}, [dispatch, dossierId, ctx.revision])

	useEffect(() => {
		const touche = (e) => {
			// Un modal ouvert par-dessus gère son Échap (il est au-dessus).
			if (
				e.key === "Escape" &&
				!document.querySelector("[data-modal-assistance]")
			)
				onClose()
		}
		document.addEventListener("keydown", touche)
		return () => document.removeEventListener("keydown", touche)
	}, [onClose])

	const etroit = useEtroit()
	// « Changer la mission liée… » : ordre en cours de liaison manuelle.
	const [changer, setChanger] = useState(null)
	const vehicule = (d && d.resume && d.resume.vehicule) || {}
	const client = (d && d.resume && d.resume.client) || {}
	const nbMissions = d ? d.missions.length : 0

	return (
		<>
			<div style={st.voile} onClick={onClose} data-assistance-ui="" />
			<aside
				style={st.tiroir}
				aria-label="Dossier d'assistance"
				data-assistance-ui=""
			>
				<div style={st.tete}>
					<div style={st.teteHaut}>
						<div style={{ minWidth: 0 }}>
							<div style={st.kicker}>
								Dossier d'assistance
								{d ? ` · ${d.assistance.label}` : ""}
							</div>
							<div style={st.titre}>
								{d ? (
									<>
										{d.assistance.label} {d.ref_affaire}
										<EtatBadge
											etat={d.statut === "clos" ? 0 : 20}
											label={
												d.statut === "clos"
													? "Clos"
													: "Ouvert"
											}
										/>
									</>
								) : (
									"…"
								)}
							</div>
						</div>
						<button
							type="button"
							style={st.fermer}
							onClick={onClose}
							aria-label="Fermer"
						>
							✕
						</button>
					</div>
					{d && (
						<div style={st.meta}>
							{(vehicule.plaques || vehicule.marque) && (
								<span>
									{vehicule.plaques ? (
										<span style={ui.plaque}>
											{vehicule.plaques}
										</span>
									) : null}
									{[vehicule.marque, vehicule.modele]
										.filter(Boolean)
										.join(" ")}
								</span>
							)}
							{(client.nom || client.prenom) && (
								<span>
									{[client.prenom, client.nom]
										.filter(Boolean)
										.join(" ")}
								</span>
							)}
							{d.fiches.map((f) => (
								<Link
									key={`fiche_${f}`}
									to={`/depannage/fiche/${f}`}
									style={{
										color: COLORS.accent,
										textDecoration: "none",
										fontWeight: 500,
									}}
									onClick={onClose}
								>
									Fiche {f} →
								</Link>
							))}
						</div>
					)}
				</div>
				<div style={st.corps}>
					{d === null && (
						<div style={{ textAlign: "center", padding: 24 }}>
							<FontAwesomeIcon icon={faSync} spin />
						</div>
					)}
					{d === false && (
						<div style={{ color: COLORS.textMuted }}>
							Dossier introuvable ou chargement impossible.
						</div>
					)}
					{d && (
						<>
							{d.mode === "ombre" && (
								<BandeauOmbre court style={{ margin: 0 }} />
							)}
							<section style={ui.carte}>
								<div style={ui.carteTitre}>
									<span>Ordres ↔ missions</span>
									<span style={ui.indice}>
										{d.ordres.length} ordre
										{d.ordres.length > 1 ? "s" : ""} ·{" "}
										{nbMissions} mission
										{nbMissions > 1 ? "s" : ""}
									</span>
								</div>
								{d.ordres.length === 0 && (
									<div
										style={{
											fontSize: 13,
											color: COLORS.textMuted,
										}}
									>
										Aucun ordre (messages d'information
										seulement).
									</div>
								)}
								{d.ordres.map((o, i) => (
									<div
										key={`ordre_${o.id}`}
										style={
											i < d.ordres.length - 1
												? {
														borderBottom: `1px solid ${COLORS.border}`,
													}
												: null
										}
									>
										<LigneOrdre
											onClose={onClose}
											ordre={o}
											chronologie={d.chronologie}
											etroit={etroit}
											onChanger={setChanger}
											peutLier={
												d.mode === "actif" ||
												ctx.ombreApplicable
											}
											interchangeables={
												(o.mission &&
													(d.interchangeables || {})[
														o.mission.id
													]) ||
												[]
											}
										/>
									</div>
								))}
							</section>
							{d.propositions.map((p) => (
								<Alerte
									key={`prop_${p.id}`}
									p={p}
									onOuvrir={() => ctx.ouvrirProposition(p)}
								/>
							))}
							<section style={ui.carte}>
								<div style={ui.carteTitre}>
									<span>Chronologie</span>
									<span
										style={{
											...ui.indice,
											display: "flex",
											gap: 12,
											flexWrap: "wrap",
										}}
									>
										<span>✉ e-mail</span>
										<span>⚙ auto</span>
										<span>
											<span
												role="img"
												aria-label="humain"
											>
												👤
											</span>{" "}
											humain
										</span>
										<span>
											<span
												role="img"
												aria-label="terrain"
											>
												🚚
											</span>{" "}
											terrain
										</span>
									</span>
								</div>
								{d.chronologie.map((e, i) => (
									<div key={`ev_${i}`} style={st.ev}>
										<span style={st.quand}>
											{dateCourte(e.date)}
										</span>
										<span style={{ textAlign: "center" }}>
											{ICONES[e.nature] || "·"}
										</span>
										<span
											style={{
												color:
													e.nature === "humain"
														? COLORS.accent
														: e.nature === "auto"
															? "#44403C"
															: COLORS.text,
												minWidth: 0,
											}}
										>
											{e.libelle}
											{e.acteur ? (
												<span
													style={{
														color: COLORS.textMuted,
													}}
												>
													{" "}
													— {e.acteur}
												</span>
											) : null}
											{e.message_id &&
											e.nature === "message" ? (
												<button
													type="button"
													style={{
														...ui.lien,
														marginLeft: 6,
														fontSize: 12,
													}}
													onClick={() =>
														ctx.ouvrirMessage(
															e.message_id,
														)
													}
												>
													voir l'e-mail
												</button>
											) : null}
										</span>
									</div>
								))}
							</section>
						</>
					)}
				</div>
			</aside>
			{changer && (
				<div data-modal-assistance="" data-assistance-ui="">
					<SelecteurCibleModal
						mode="ordre"
						ordreId={changer.id}
						titreOrdre={`${
							changer.mission
								? "Changer la mission liée à"
								: "Lier"
						} l'ordre ${changer.ref_ordre || changer.role_label}…`}
						onClose={() => setChanger(null)}
						onDone={() => {
							setChanger(null)
							ctx.actualiser()
						}}
					/>
				</div>
			)}
		</>
	)
}

const st = {
	interchangeable: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		padding: "0 0 10px",
	},
	voile: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(28, 25, 23, 0.35)",
		backdropFilter: "blur(2px)",
		zIndex: 900,
	},
	tiroir: {
		position: "fixed",
		top: 0,
		right: 0,
		bottom: 0,
		width: "min(620px, 100vw)",
		backgroundColor: COLORS.bg,
		borderLeft: `1px solid ${COLORS.border}`,
		boxShadow: "-24px 0 48px rgba(28, 25, 23, 0.12)",
		zIndex: 910,
		display: "flex",
		flexDirection: "column",
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
		color: COLORS.text,
	},
	tete: {
		backgroundColor: COLORS.surface,
		padding: "18px 22px",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	teteHaut: {
		display: "flex",
		justifyContent: "space-between",
		gap: 12,
		alignItems: "flex-start",
	},
	kicker: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
	},
	titre: {
		fontSize: 20,
		fontWeight: 600,
		letterSpacing: "-0.02em",
		marginTop: 2,
		display: "flex",
		gap: 10,
		alignItems: "center",
		flexWrap: "wrap",
	},
	meta: {
		marginTop: 8,
		fontSize: 13,
		color: COLORS.textMuted,
		display: "flex",
		gap: "6px 14px",
		flexWrap: "wrap",
		alignItems: "center",
	},
	fermer: {
		width: 32,
		height: 32,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		background: "transparent",
		color: COLORS.textMuted,
		cursor: "pointer",
		fontSize: 15,
		flexShrink: 0,
	},
	corps: {
		overflow: "auto",
		padding: "16px 22px 40px",
		display: "flex",
		flexDirection: "column",
		gap: 14,
	},
	om: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1.15fr) 22px minmax(0, 1fr)",
		gap: 8,
		alignItems: "center",
		padding: "10px 0",
	},
	r1: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
	r2: { fontSize: 12.5, color: COLORS.textMuted, marginTop: 3 },
	fleche: { color: COLORS.textLight, textAlign: "center" },
	omEtroit: {
		display: "flex",
		flexDirection: "column",
		gap: 6,
		padding: "10px 0",
	},
	flecheEtroite: { color: COLORS.textLight, paddingLeft: 2 },
	mchip: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		padding: "7px 10px",
		fontSize: 12.5,
		backgroundColor: COLORS.surface,
	},
	m1: {
		display: "flex",
		gap: 6,
		alignItems: "center",
		flexWrap: "wrap",
		fontWeight: 700,
	},
	m2: { color: COLORS.textMuted, marginTop: 2 },
	mchipVide: {
		border: `1px dashed ${COLORS.border}`,
		borderRadius: 10,
		padding: "7px 10px",
		fontSize: 12.5,
		color: COLORS.textLight,
	},
	mchipAttente: {
		border: "1px dashed #FCD34D",
		borderRadius: 10,
		padding: "7px 10px",
		fontSize: 12.5,
		backgroundColor: COLORS.warningLight,
		color: "#92400E",
	},
	alerte: {
		display: "flex",
		gap: 12,
		alignItems: "center",
		padding: "12px 14px",
		borderRadius: 12,
		border: `1px solid ${COLORS.dangerBorder}`,
		backgroundColor: COLORS.dangerLight,
	},
	ev: {
		display: "grid",
		gridTemplateColumns: "84px 22px minmax(0, 1fr)",
		gap: 8,
		padding: "7px 0",
		fontSize: 13,
		alignItems: "start",
	},
	quand: {
		color: COLORS.textMuted,
		fontSize: 12,
		paddingTop: 1,
		whiteSpace: "nowrap",
	},
}

export default DossierPanneau
